import { isCourseClaveRule } from "../../../components/Forms/GameQuizForm/yamlImport";
import type { YamlImportResult } from "../../../components/Forms/GameQuizForm/yamlImport";
import type { GameWithTags } from "../../../services/gameQuiz/gameQuiz.types";
import type { DocCheckIssue } from "./hubTypes";
import { asNotice, normalizeClave, normalizeTitle } from "./rowState";
import type { ReadyRow } from "./rowState";

// The hub's own issues put the name of the field in `text`; only the
// validator's issues carry an excerpt worth repeating.
const FIELD_LABELS = new Set(["documento", "curso #", "título"]);

function issueMessage({ issue, text }: DocCheckIssue): string {
  const excerpt = text.trim();
  const lowered = excerpt.toLowerCase();
  if (
    !excerpt ||
    FIELD_LABELS.has(lowered) ||
    issue.toLowerCase().includes(lowered)
  ) {
    return issue;
  }
  return `${issue} («${excerpt}»)`;
}

// A course that already has lessons is no error since SCRUM-1532: the OD
// replaces them in place.
export function docMessages(issues: DocCheckIssue[]): string[] {
  return issues.filter((i) => i.severity === "error").map(issueMessage);
}

export const docWithoutCursoNotice = (courseId: number): string =>
  asNotice(
    `el documento no tiene «Curso #»; se usa el ID del tracker (${courseId}).`,
  );

// The importer builds these questions without zones (and every label of an
// image_labeling one unassigned): the editor draws them by hand. They go up
// published anyway, and the notice is the list of what a person must finish.
const IMAGE_QUESTION_TODO = {
  image_hotspot: "no tiene zonas; hay que marcarlas a mano en el editor",
  image_labeling:
    "no tiene zonas ni etiquetas asignadas; hay que completarlas a mano en el editor",
} as const;

const imageQuestionNotices = (games: GameWithTags[]): string[] =>
  games.flatMap((game, index) =>
    game.type === "image_hotspot" || game.type === "image_labeling"
      ? [
          asNotice(
            `la pregunta ${index + 1} (${game.type}) ${IMAGE_QUESTION_TODO[game.type]}.`,
          ),
        ]
      : [],
  );

// Most tracker YAMLs carry no `clave`; they are named `<clave>_QUIZ.yml`, so
// the name says which row they belong to.
function nameCarriesClave(fileName: string, rowClave: string): boolean {
  const clave = normalizeClave(rowClave);
  const name = fileName.toUpperCase();
  const next = name.charAt(clave.length);
  return (
    clave !== "" && name.startsWith(clave) && (next === "_" || next === ".")
  );
}

type QuizRow = Pick<ReadyRow, "clave" | "courseTitle" | "courseId">;

// The clave only ties a YAML to its row. The quiz goes to the row's own course
// whatever it says, so a clave that does not tie is a notice, never an error.
function claveNotice(
  parsed: YamlImportResult,
  row: QuizRow,
  fileName: string,
): string | null {
  const useTracker = `se usa el ID del tracker (${row.courseId}).`;
  if (normalizeClave(row.clave) === "") {
    return asNotice(`la fila no tiene clave; ${useTracker}`);
  }
  if (!parsed.clave) {
    return nameCarriesClave(fileName, row.clave)
      ? null
      : asNotice(`el YAML no tiene clave; ${useTracker}`);
  }
  return normalizeClave(parsed.clave) === normalizeClave(row.clave)
    ? null
    : asNotice(
        `la clave del YAML (${parsed.clave}) no coincide con la de la fila (${row.clave}); ${useTracker}`,
      );
}

export function quizYamlMessages(
  parsed: YamlImportResult,
  row: QuizRow,
  fileName: string,
): { errors: string[]; notices: string[] } {
  // The form needs a `clave` to assign the quiz; the hub assigns it to the
  // row's own course, so that rule is not an error here.
  const errors = parsed.errors.filter((e) => !isCourseClaveRule(e));
  // The parser already reports a missing title.
  if (
    parsed.title &&
    normalizeTitle(parsed.title) !== normalizeTitle(row.courseTitle)
  ) {
    errors.push(
      `El título del YAML («${parsed.title}») no coincide con el curso de la fila («${row.courseTitle}»).`,
    );
  }
  const clave = claveNotice(parsed, row, fileName);
  return {
    errors,
    notices: [...(clave ? [clave] : []), ...imageQuestionNotices(parsed.games)],
  };
}
